/**
 * C7's manufacturing routes (design §5.5).
 *
 * A separate file spread into `MFG_ROUTES`, for the same reason
 * `ap/routesC3.ts` exists: a milestone's additions are reviewable as a block and
 * a conflict in one milestone's routes cannot silently drop another's.
 *
 * Every `:id` segment carries the `\d+` constraint so no literal word can be
 * parsed as a document id.
 *
 * **Which permission guards which screen follows the API, not the topic.** The
 * settlement preview is `mfg.settlement.preview` and not
 * `mfg.production_order.close`, because computing what an order *would* settle
 * to is read-shaped and deliberately not the same authority as posting it — a
 * supervisor may want to see the residual without being the person who plugs it.
 * The roll-up board reads with `mfg.standard_cost.read` and computes with
 * `mfg.standard_cost.rollup` (step-up MFA), so the people measured against a
 * standard can see it without being able to move it.
 */

import type { RouteRecordRaw } from 'vue-router'

import { MFG_PERMISSIONS } from '@/modules/mfg/permissions'

export const MFG_C7_ROUTES: RouteRecordRaw[] = [
  /* --------------------------------------------------------------------- ECO */
  {
    path: '/mfg/engineering-change-orders',
    name: 'mfg-ecos',
    component: () => import('@/modules/mfg/eco/EcoListView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.ecos', permission: MFG_PERMISSIONS.ecoRead },
  },
  {
    path: '/mfg/engineering-change-orders/:id(\\d+)',
    name: 'mfg-eco',
    component: () => import('@/modules/mfg/eco/EcoView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.eco', permission: MFG_PERMISSIONS.ecoRead },
  },
  {
    path: '/mfg/bom-lines/:id(\\d+)/effectivity',
    name: 'mfg-bom-line-effectivity',
    component: () => import('@/modules/mfg/eco/EffectivityView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.effectivity', permission: MFG_PERMISSIONS.bomRead },
  },
  {
    path: '/mfg/boms/:id(\\d+)/outputs',
    name: 'mfg-bom-outputs',
    component: () => import('@/modules/mfg/eco/BomOutputsView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.bomOutputs', permission: MFG_PERMISSIONS.bomRead },
  },

  /* ------------------------------------------------- substitution, genealogy */
  {
    path: '/mfg/production-orders/:id(\\d+)/substitutions',
    name: 'mfg-order-substitutions',
    component: () => import('@/modules/mfg/production/SubstitutionsView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.substitutions', permission: MFG_PERMISSIONS.orderRead },
  },
  {
    path: '/mfg/production-orders/:id(\\d+)/genealogy',
    name: 'mfg-order-genealogy',
    component: () => import('@/modules/mfg/production/GenealogyView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.genealogy', permission: MFG_PERMISSIONS.orderRead },
  },

  /* ----------------------------------------------------------------- capacity */
  {
    path: '/mfg/work-centers/:id(\\d+)/calendar',
    name: 'mfg-work-center-calendar',
    component: () => import('@/modules/mfg/capacity/WorkCenterCalendarView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.calendar', permission: MFG_PERMISSIONS.capacityRead },
  },
  {
    path: '/mfg/work-centers/:id(\\d+)/dispatch-list',
    name: 'mfg-dispatch-list',
    component: () => import('@/modules/mfg/capacity/DispatchListView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.dispatch', permission: MFG_PERMISSIONS.capacityRead },
  },
  {
    path: '/mfg/schedule-runs',
    name: 'mfg-schedule-runs',
    component: () => import('@/modules/mfg/capacity/ScheduleRunsView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.scheduleRuns', permission: MFG_PERMISSIONS.capacityRead },
  },
  {
    path: '/mfg/reports/bottlenecks',
    name: 'mfg-bottlenecks',
    component: () => import('@/modules/mfg/capacity/BottlenecksView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.bottlenecks', permission: MFG_PERMISSIONS.capacityRead },
  },
  {
    path: '/mfg/tools',
    name: 'mfg-tools',
    component: () => import('@/modules/mfg/capacity/ToolsView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.tools', permission: MFG_PERMISSIONS.capacityRead },
  },

  /* ----------------------------------------------------------------- planning */
  {
    path: '/mfg/planning-parameters',
    name: 'mfg-planning-parameters',
    component: () => import('@/modules/mfg/planning/PlanningParametersView.vue'),
    meta: {
      titleKey: 'mfg.c7.routeTitles.planningParameters',
      permission: MFG_PERMISSIONS.planningManage,
    },
  },
  {
    path: '/mfg/mrp-runs/:id(\\d+)',
    name: 'mfg-mrp-run',
    component: () => import('@/modules/mfg/planning/MrpRunView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.mrpRun', permission: MFG_PERMISSIONS.mrpRead },
  },
  {
    path: '/mfg/mrp-suggestions/:id(\\d+)/pegging',
    name: 'mfg-pegging',
    component: () => import('@/modules/mfg/planning/PeggingView.vue'),
    meta: { titleKey: 'mfg.c7.routeTitles.pegging', permission: MFG_PERMISSIONS.mrpRead },
  },

  /* ------------------------------------------------------------------ costing */
  {
    path: '/mfg/production-orders/:id(\\d+)/settlement',
    name: 'mfg-settlement',
    component: () => import('@/modules/mfg/costing/SettlementPreviewView.vue'),
    meta: {
      titleKey: 'mfg.c7.routeTitles.settlement',
      permission: MFG_PERMISSIONS.settlementPreview,
    },
  },
  {
    path: '/mfg/standard-cost-rollups',
    name: 'mfg-rollups',
    component: () => import('@/modules/mfg/costing/RollupBoardView.vue'),
    meta: {
      titleKey: 'mfg.c7.routeTitles.rollups',
      permission: MFG_PERMISSIONS.standardCostRead,
    },
  },
  {
    path: '/mfg/standard-cost-rollups/:id(\\d+)',
    name: 'mfg-rollup',
    component: () => import('@/modules/mfg/costing/RollupView.vue'),
    meta: {
      titleKey: 'mfg.c7.routeTitles.rollup',
      permission: MFG_PERMISSIONS.standardCostRead,
    },
  },
  {
    path: '/mfg/reports/variance-analysis',
    name: 'mfg-variance-analysis',
    component: () => import('@/modules/mfg/costing/VarianceAnalysisView.vue'),
    meta: {
      titleKey: 'mfg.c7.routeTitles.varianceAnalysis',
      permission: MFG_PERMISSIONS.varianceRead,
    },
  },
]

/** The same set as strings, for links inside the module. */
export const MFG_C7_PATHS = {
  ecos: '/mfg/engineering-change-orders',
  eco: (id: number) => `/mfg/engineering-change-orders/${id}`,
  effectivity: (bomLineId: number) => `/mfg/bom-lines/${bomLineId}/effectivity`,
  bomOutputs: (bomId: number) => `/mfg/boms/${bomId}/outputs`,
  substitutions: (orderId: number) => `/mfg/production-orders/${orderId}/substitutions`,
  genealogy: (orderId: number) => `/mfg/production-orders/${orderId}/genealogy`,
  calendar: (workCenterId: number) => `/mfg/work-centers/${workCenterId}/calendar`,
  dispatch: (workCenterId: number) => `/mfg/work-centers/${workCenterId}/dispatch-list`,
  scheduleRuns: '/mfg/schedule-runs',
  bottlenecks: '/mfg/reports/bottlenecks',
  tools: '/mfg/tools',
  planningParameters: '/mfg/planning-parameters',
  mrpRun: (id: number) => `/mfg/mrp-runs/${id}`,
  pegging: (suggestionId: number) => `/mfg/mrp-suggestions/${suggestionId}/pegging`,
  settlement: (orderId: number) => `/mfg/production-orders/${orderId}/settlement`,
  rollups: '/mfg/standard-cost-rollups',
  rollup: (id: number) => `/mfg/standard-cost-rollups/${id}`,
  varianceAnalysis: '/mfg/reports/variance-analysis',
} as const
